// src/components/layout/MobileMenu.js

import React from 'react';
import { X } from 'lucide-react';

/** Badge numérico compacto */
const NavBadge = ({ count, className = '' }) => {
    if (!count || count <= 0) return null;
    return (
        <span className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-xs font-black bg-[#D20000] text-white leading-none ${className}`}>
            {count > 99 ? '99+' : count}
        </span>
    );
};

/**
 * Navegação superior — Desktop
 */
export const NavigationTop = ({ activeView, onNavigate, navItems }) => (
    <nav className="hidden md:flex bg-[#002B70] px-6 py-2 justify-center space-x-2 sticky top-[65px] z-20 shadow-sm">
        {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
                <button
                    key={item.id}
                    onClick={() => onNavigate(item.id)}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all duration-200 ${
                        isActive
                            ? 'bg-[#D20000] text-white shadow-md scale-105'
                            : 'text-blue-100 hover:bg-white/10 hover:text-white'
                    }`}
                >
                    <Icon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-blue-200'}`} />
                    {item.label}
                    <NavBadge count={item.badge} className="ml-0.5" />
                </button>
            );
        })}
    </nav>
);

/**
 * Menu Mobile — Drawer lateral + barra inferior fixa
 */
export const MobileMenu = ({ activeView, onNavigate, navItems, isOpen, onClose }) => (
    <>
        {/* Overlay */}
        {isOpen && (
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 transition-opacity md:hidden"
                onClick={onClose}
            />
        )}

        {/* Drawer lateral */}
        <div className={`fixed inset-y-0 left-0 w-72 bg-white shadow-md transform transition-transform duration-300 ease-in-out z-50 md:hidden flex flex-col ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
            <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-[#002B70]">
                <span className="font-black text-white text-lg tracking-wide">MENU RPLAY</span>
                <button onClick={onClose} className="p-2 text-white hover:bg-white/20 rounded-full transition-colors">
                    <X className="w-5 h-5" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4 bg-gray-50">
                <div className="space-y-2 px-4">
                    {navItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = activeView === item.id;
                        return (
                            <button
                                key={item.id}
                                onClick={() => { onNavigate(item.id); onClose(); }}
                                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm font-bold transition-all ${
                                    isActive
                                        ? 'bg-[#D20000] text-white shadow-md'
                                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-100 shadow-sm'
                                }`}
                            >
                                <span className="flex items-center gap-3">
                                    <Icon className={`w-5 h-5 ${isActive ? 'text-white' : 'text-gray-400'}`} />
                                    {item.label}
                                </span>
                                <NavBadge count={item.badge} />
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>

        {/* Barra inferior fixa (Mobile) */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 bg-[#002B70] border-t border-[#001a4d] px-4 py-2 flex justify-around items-center z-30 pb-safe shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
            {navItems.slice(0, 4).map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                return (
                    <button
                        key={item.id}
                        onClick={() => onNavigate(item.id)}
                        className={`relative flex flex-col items-center justify-center w-full py-2 rounded-lg transition-all duration-200 ${
                            isActive ? 'bg-[#D20000] text-white -translate-y-1 shadow-sm' : 'text-blue-200 hover:text-white hover:bg-white/10'
                        }`}
                    >
                        <div className="relative">
                            <Icon className={`w-5 h-5 mb-0.5 ${isActive ? 'fill-current' : ''}`} />
                            {item.badge > 0 && (
                                <span className="absolute -top-1.5 -right-2 min-w-[14px] h-3.5 px-0.5 rounded-full text-[8px] font-black bg-yellow-400 text-yellow-900 flex items-center justify-center leading-none">
                                    {item.badge > 9 ? '9+' : item.badge}
                                </span>
                            )}
                        </div>
                        <span className="text-[10px] font-bold truncate max-w-[60px]">{item.label}</span>
                    </button>
                );
            })}
        </div>
    </>
);
